import { MetricOption } from "./types";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
  faTruck,
  faDollarSign,
  faShoppingCart,
  faBullseye,
  faMapMarkerAlt,
  faClock,
  faArrowTrendUp,
} from "@fortawesome/free-solid-svg-icons";
import { DeliverySummaryData, KeyMetric } from "./types";

export const METRIC_OPTIONS: Array<{ label: string; value: MetricOption }> = [
  { label: "Orders", value: "orders" },
  { label: "Revenue", value: "revenue" },
  { label: "Cost", value: "cost" },
  { label: "Profit", value: "profit" },
  { label: "Avg Distance", value: "avgDistance" },
];

export const EMPTY_STATE_MESSAGES = {
  noDeliveryData: "No delivery data available for the selected period",
} as const;

export const SUMMARY_TEXTS = {
  title: "Delivery Cost Analysis Summary",
  description: "Comprehensive overview of delivery costs, revenue, and profitability analysis.",
  marginLabel: "% margin",
} as const;

export const addCommasInPrices = (value: number | string): string => {
  return (parseFloat(String(value)) || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export const METRIC_CONFIG: Record<
  string,
  {
    title: string;
    subTitle: string;
    icon: IconDefinition;
    color: string;
    formatValue: (value: number) => string;
  }
> = {
  totalDeliveryOrders: {
    title: "Total Delivery Orders",
    subTitle: "Number of delivery orders",
    icon: faTruck,
    color: "#1890ff",
    formatValue: (value: number) => value.toString(),
  },
  totalDeliveryRevenue: {
    title: "Delivery Revenue",
    subTitle: "Total delivery revenue",
    icon: faDollarSign,
    color: "#52c41a",
    formatValue: (value: number) => `$${addCommasInPrices(value.toFixed(2))}`,
  },
  totalDeliveryCost: {
    title: "Delivery Cost",
    subTitle: "Total delivery cost",
    icon: faShoppingCart,
    color: "#ff4d4f",
    formatValue: (value: number) => `$${addCommasInPrices(value.toFixed(2))}`,
  },
  totalDeliveryFees: {
    title: "Delivery Fees",
    subTitle: "Total delivery fees",
    icon: faBullseye,
    color: "#fa8c16",
    formatValue: (value: number) => `$${addCommasInPrices(value.toFixed(2))}`,
  },
  averageDeliveryDistance: {
    title: "Avg Distance",
    subTitle: "Average delivery distance",
    icon: faMapMarkerAlt,
    color: "#722ed1",
    formatValue: (value: number) => `${value.toFixed(1)} km`,
  },
  averageDeliveryTime: {
    title: "Avg Time",
    subTitle: "Average delivery time",
    icon: faClock,
    color: "#13c2c2",
    formatValue: (value: number) => `${value.toFixed(0)} min`,
  },
};

export const ARROW_ICON_CONFIG = {
  icon: faArrowTrendUp,
  color: "#52c41a",
  fontSize: "20px",
} as const;

export const SKELETON_CONFIG = {
  metricCardsCount: 6,
  skeletonRows: 2,
} as const;

export const generateKeyMetrics = (summary: DeliverySummaryData): KeyMetric[] => {
  const metrics: KeyMetric[] = [];

  Object.entries(METRIC_CONFIG).forEach(([key, config]) => {
    const value = summary[key as keyof DeliverySummaryData] as number;
    if (value !== undefined) {
      metrics.push({
        id: key,
        title: config.title,
        subTitle: config.subTitle,
        value: config.formatValue(value),
        icon: config.icon,
        color: config.color,
      });
    }
  });

  return metrics;
};
